import React from "react";
import { NeoButton } from "../../../../../components/Common";
import { FieldArray } from "formik";
import { DEFAULT_SET_BY_TYPE, WORKOUT_TYPES } from "../../constants";
import BlockCard from "./BlockCard";
import { useTenant } from "../../../../../hooks/useTenant";
import { useBlockLibrary } from "../../hooks/useBlockLibrary";

const BRANCH_TO_WORKOUT_TYPE = {
    musculacao: "strength",
    crossfit: "wod",
    natacao: "swimming",
    corrida: "running",
};

const buildDefaultBlock = (defaultWorkoutType = "strength", index = 0) => ({
    id: Date.now().toString() + index,
    name: defaultWorkoutType === "wod" ? "WOD" : "Bloco 1",
    workoutType: defaultWorkoutType,
    method: defaultWorkoutType === "strength" ? "normal" : "",
    blockType: defaultWorkoutType === "running" ? "main" : "",
    repeat: defaultWorkoutType === "running" ? 1 : "",
    restBetweenRepeats: "",
    sessionType: defaultWorkoutType === "running" ? "easy" : "",
    locationType: defaultWorkoutType === "running" ? "street" : "",
    terrain: defaultWorkoutType === "running" ? "flat" : "",
    componentType: defaultWorkoutType === "wod" ? "metcon" : "",
    format: defaultWorkoutType === "wod" ? "amrap" : "",
    scoreType: defaultWorkoutType === "wod" ? "rounds_reps" : "",
    timeCap: defaultWorkoutType === "wod" ? "20 min" : "",
    rounds: "",
    stimulus: "",
    scalingNotes: "",
    sets: [{ id: (Date.now() + 1).toString(), ...DEFAULT_SET_BY_TYPE[defaultWorkoutType] }]
});

const AutoAddBlock = ({ push, defaultWorkoutType = "strength" }) => {
    React.useEffect(() => {
        push(buildDefaultBlock(defaultWorkoutType));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return null;
};

/**
 * Editor de blocos de treino — orquestrador.
 * Gerencia a lista de BlockCards e o botão "Adicionar Bloco".
 *
 * @param {Array}    blocks             - Array de blocos do formik values
 * @param {string}   namePrefix         - Prefixo formik (e.g. "blocks")
 * @param {Function} handleChange       - Formik handleChange
 * @param {Function} setFieldValue      - Formik setFieldValue
 * @param {string}   defaultWorkoutType - Tipo de treino padrão
 * @param {Array}    runningPaceGroups  - Grupos de pace para corrida
 * @param {Function} onLibraryChanged   - Callback quando lib muda
 */
const BlocksEditor = ({ blocks = [], namePrefix = "blocks", handleChange, setFieldValue, defaultWorkoutType = "strength", runningPaceGroups = [], onLibraryChanged }) => {
    const { primaryBranch = [] } = useTenant();

    const allowedWorkoutTypes = primaryBranch.length > 0
        ? primaryBranch.map(b => BRANCH_TO_WORKOUT_TYPE[b]).filter(Boolean)
        : null;

    const visibleWorkoutTypes = allowedWorkoutTypes
        ? WORKOUT_TYPES.filter(t => allowedWorkoutTypes.includes(t.value))
        : WORKOUT_TYPES;

    const libraryHandlers = useBlockLibrary({ namePrefix, setFieldValue, onLibraryChanged });

    return (
        <FieldArray name={namePrefix}>
            {({ push, remove }) => (
                <div>
                    {blocks.length === 0 ? (
                        <AutoAddBlock push={push} defaultWorkoutType={defaultWorkoutType} />
                    ) : (
                        <div className="d-flex flex-column gap-3">
                            {blocks.map((block, blockIndex) => (
                                <BlockCard
                                    key={block.id || blockIndex}
                                    block={block}
                                    blockIndex={blockIndex}
                                    namePrefix={namePrefix}
                                    defaultWorkoutType={defaultWorkoutType}
                                    handleChange={handleChange}
                                    setFieldValue={setFieldValue}
                                    visibleWorkoutTypes={visibleWorkoutTypes}
                                    onDuplicate={() => libraryHandlers.duplicateBlock(push, block, blockIndex)}
                                    onDelete={() => remove(blockIndex)}
                                    libraryHandlers={libraryHandlers}
                                    runningPaceGroups={runningPaceGroups}
                                />
                            ))}

                            <NeoButton
                                type="button"
                                variant="soft-primary"
                                icon="mdi-plus-circle-outline"
                                className="w-100"
                                onClick={() => push({
                                    ...buildDefaultBlock(defaultWorkoutType, blocks.length + 1),
                                    name: defaultWorkoutType === "wod" ? `Componente ${blocks.length + 1}` : `Bloco ${blocks.length + 1}`,
                                    sets: []
                                })}
                            >
                                Adicionar Bloco
                            </NeoButton>
                        </div>
                    )}
                </div>
            )}
        </FieldArray>
    );
};

export default BlocksEditor;
